import type { MediaRef } from "@aurumiq/cms-schema";
import { Button } from "@aurumiq/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@aurumiq/ui/components/dialog";
import { formatBytes } from "@/features/releases/artifacts";
import type { CmsMedia } from "@/lib/types";
import { useMedia } from "./queries";

export function toMediaRef(m: CmsMedia): MediaRef {
  return {
    id: m.id,
    url: m.url,
    alt: m.alt ?? "",
    ...(m.width ? { width: m.width } : {}),
    ...(m.height ? { height: m.height } : {}),
  };
}

export function MediaThumb({
  media,
  className,
}: {
  media: Pick<CmsMedia, "url" | "mime" | "alt" | "filename">;
  className?: string;
}) {
  const isImage = media.mime.startsWith("image/");
  return isImage ? (
    <img src={media.url} alt={media.alt || media.filename} className={className} loading="lazy" />
  ) : (
    <div
      className={`${className ?? ""} grid place-items-center bg-muted font-mono text-muted-foreground text-xs`}
    >
      {media.mime}
    </div>
  );
}

/** Grid of library media; picking one returns a cms-schema MediaRef. */
export function MediaPickerDialog({
  open,
  onOpenChange,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (ref: MediaRef) => void;
}) {
  const media = useMedia(1);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Choose media</DialogTitle>
          <DialogDescription>Upload new files from Marketing → Media.</DialogDescription>
        </DialogHeader>
        <div className="grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 md:grid-cols-4">
          {media.isPending ? (
            <p className="col-span-full text-muted-foreground text-sm">Loading…</p>
          ) : media.isError ? (
            <p className="col-span-full text-danger text-sm">Could not load media.</p>
          ) : media.data.items.length === 0 ? (
            <p className="col-span-full text-muted-foreground text-sm">The library is empty.</p>
          ) : (
            media.data.items.map((m) => (
              <button
                key={m.id}
                type="button"
                className="group grid gap-1 rounded-lg border p-1.5 text-left hover:border-primary focus-visible:border-primary"
                onClick={() => {
                  onPick(toMediaRef(m));
                  onOpenChange(false);
                }}
                aria-label={`Pick ${m.filename}`}
              >
                <MediaThumb media={m} className="aspect-video w-full rounded object-cover" />
                <span className="truncate text-xs">{m.filename}</span>
                <span className="text-[11px] text-muted-foreground">
                  {m.width && m.height ? `${m.width}×${m.height} · ` : ""}
                  {formatBytes(m.size)}
                </span>
              </button>
            ))
          )}
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
